import React, { useEffect, useState } from "react";
import {
  Button,
  TextInput,
  Label,
  Header,
  DataGrid,
  DataGridColumn,
  ProgressBar,
  useValidatedState,
} from "acma-spa";
import NumberFormat from "react-number-format";
import * as yup from "yup";
import { getApiClient, IInvoiceResponse, IPaymentResponse } from "../api";
import { NavPage } from "../models/NavPage";

export interface SummaryFormProps {
  baseUrl: string;
  receipt: IPaymentResponse;
  setPageState: (page: NavPage) => void;
}

class Email {
  email: string;
}

export const ConfirmationForm: React.FC<SummaryFormProps> = (props): any => {
  const emailSchema: yup.ObjectSchema<Email> = yup.object({
    email: yup.string().email().required(),
  });

  const [email, setEmail, errors, validate] = useValidatedState<Email>(
    { email: "" },
    emailSchema
  );
  const sendEmailReceipt = async () => {
    await validate("#container").then(async (result) => {
      if (result) {
        let secret = props.receipt.secret;
        if (typeof secret === "undefined") return;

        try {
          setEmailSent(false);
          setLoading(true);
          const [client, abort] = getApiClient(props.baseUrl);
          let resp = await client.sendEmailReceipt(email.email, secret);
          if (resp) setEmailSent(true);
        } catch (e) {
          alert("Unexpected error");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const [downloading, setDownloading] = useState<boolean>(false);
  const downloadReceipt = async () => {
    let secret = props.receipt.secret;
    let receipt = props.receipt.bankTransactionId;
    if (!secret || !receipt) return;

    try {
      setDownloading(true);
      let resp = await fetch(
        `${props.baseUrl}/Invoices/DownloadReceipt?Secret=${secret}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      if (resp.status !== 200) {
        alert("Unexpected error");
      }
      let blob = await resp.blob();
      const objectUrl = window.URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = objectUrl;
      link.target = "_blank";
      link.setAttribute("download", receipt + ".pdf");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      if (error instanceof Error) alert("Unexpected error");
    } finally {
      setDownloading(false);
    }
  };

  const [rows, setRows] = useState<IInvoiceResponse[]>([]);
  const columns: DataGridColumn<IInvoiceResponse>[] = [
    { label: "Items", value: (r) => r.invoiceNumber, footer: "Total" },
    {
      label: "Net",
      value: (r) => (
        <NumberFormat
          value={r.exclAmount}
          displayType={"text"}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
        />
      ),
      footer: (
        <NumberFormat
          value={rows.reduce((a, b) => a + b.exclAmount, 0)}
          displayType={"text"}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
        />
      ),
    },
    {
      label: "GST",
      value: (r) => (
        <NumberFormat
          value={r.taxAmount}
          displayType={"text"}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
        />
      ),
      footer: (
        <NumberFormat
          value={rows.reduce((a, b) => a + b.taxAmount, 0)}
          displayType={"text"}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
        />
      ),
    },
    {
      label: "Total",
      value: (r) => (
        <NumberFormat
          value={r.amount}
          displayType={"text"}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
        />
      ),
      footer: (
        <NumberFormat
          value={rows.reduce((a, b) => a + b.amount, 0)}
          displayType={"text"}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
        />
      ),
    },
  ];

  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    setRows(props.receipt.invoiceResponses);
  }, []);

  return (
    <>
      <div id="container">
        <ProgressBar total={2} current={2} />
        <div className="pb-3">
          <h2>Make a payment</h2>
          <Header label="Payment summary." className="py-3 mb-3" />
          <div className="row">
            <div className="col mb-2">
              <b>Transaction successful.</b>
            </div>
          </div>
          <div className="row">
            <div className="col mb-2">
              <b>Please keep this receipt for your records.</b>
            </div>
          </div>
          <div className="p-3 bg-white">
            <div className="row">
              <div className="col mb-2">
                <b>RECEIPT</b>
              </div>
            </div>
            <div className="row">
              <div className="col mb-1">
                <b>Australian Communications and Media Authority</b>
              </div>
            </div>
            <div className="row">
              <div className="col mb-1">ABN: 55 386 169 386</div>
            </div>
            <div className="row">
              <div className="col mb-3"></div>
            </div>
            <div className="row">
              <div className="col mb-1">PO Box 78</div>
            </div>
            <div className="row">
              <div className="col mb-1">Belconnen ACT 2616</div>
            </div>
            <div className="row">
              <div className="col mb-1">Phone: 1300 850 115</div>
            </div>
            <div className="row">
              <div className="col mb-3"></div>
            </div>
            <div className="row">
              <div className="col mb-1">
                <b>Receipt Number: {props.receipt.bankTransactionId}</b>
              </div>
            </div>
            <div className="row">
              <div className="col mb-1">
                <b>
                  Date:{" "}
                  {props && props.receipt && props.receipt.createdAt
                    ? new Date(props.receipt.createdAt).toLocaleString("en-AU")
                    : ""}
                </b>
              </div>
            </div>
            {rows && (
              <DataGrid<IInvoiceResponse> columns={columns} rows={rows} />
            )}
            <div className="row">
              <div className="col">
                <Label
                  id="email"
                  label="Send receipt by email"
                  className="mb-3"
                  error={errors.email}
                >
                  <div className="row">
                    <div className="col-md-auto mt-2">
                      <TextInput
                        value={email.email}
                        placeholder="Email address"
                        onChange={(v) => setEmail({ email: v })}
                        size={"block"}
                      />
                    </div>
                    <div className="col-md mt-2">
                      <Button
                        disabled={loading}
                        label="Send"
                        onClick={sendEmailReceipt}
                        className="mr-2"
                      />
                      <Button
                        disabled={downloading}
                        mode="secondary"
                        label="Download"
                        onClick={downloadReceipt}
                        className="mr-2"
                      />
                      {emailSent ? <span> Email Sent!</span> : <></>}
                    </div>
                  </div>
                </Label>
              </div>
            </div>
          </div>
          <div className="row mt-3">
            <div className="col mb-2">
              <a href="">Make another payment</a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
